<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { ChevronsUpDown, FolderOpen, Plus } from 'lucide-vue-next';
import { ref } from 'vue';
import CreateProjectDialog from '@/components/CreateProjectDialog.vue';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from '@/components/ui/sidebar';
import * as projectRoutes from '@/routes/projects';
import type { SidebarProject } from '@/types';

defineProps<{
    projects: SidebarProject[];
    currentProject: { id: string; name: string } | null;
}>();

const { isMobile, state } = useSidebar();

const showCreateDialog = ref(false);
</script>

<template>
    <SidebarMenu>
        <SidebarMenuItem>
            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <SidebarMenuButton
                        size="lg"
                        class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                        :tooltip="
                            currentProject?.name ?? 'Sélectionner un projet'
                        "
                    >
                        <div
                            class="flex aspect-square size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"
                        >
                            <FolderOpen class="size-4" />
                        </div>
                        <div
                            class="grid flex-1 text-left text-sm leading-tight"
                        >
                            <span class="truncate text-xs text-muted-foreground"
                                >Projet</span
                            >
                            <span class="truncate font-semibold">
                                {{
                                    currentProject?.name ??
                                    'Sélectionner un projet'
                                }}
                            </span>
                        </div>
                        <ChevronsUpDown class="ml-auto size-4 shrink-0" />
                    </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    class="w-(--reka-dropdown-menu-trigger-width) min-w-64 rounded-lg"
                    :side="
                        isMobile
                            ? 'bottom'
                            : state === 'collapsed'
                              ? 'right'
                              : 'bottom'
                    "
                    align="start"
                    :side-offset="4"
                >
                    <DropdownMenuLabel class="text-xs text-muted-foreground">
                        Mes projets
                    </DropdownMenuLabel>
                    <DropdownMenuGroup>
                        <DropdownMenuItem
                            v-for="project in projects"
                            :key="project.id"
                            :as-child="true"
                        >
                            <Link
                                :href="projectRoutes.show(project.id).url"
                                class="flex w-full cursor-pointer items-center gap-2"
                            >
                                <div
                                    class="size-1.5 shrink-0 rounded-full"
                                    :class="
                                        currentProject?.id === project.id
                                            ? 'bg-sidebar-primary'
                                            : 'bg-muted-foreground/40'
                                    "
                                />
                                <div
                                    class="flex flex-1 flex-col gap-0 overflow-hidden"
                                >
                                    <span class="truncate font-medium">{{
                                        project.name
                                    }}</span>
                                    <span class="text-xs text-muted-foreground">
                                        {{ project.papers_count ?? 0 }}
                                        article{{
                                            (project.papers_count ?? 0) !== 1
                                                ? 's'
                                                : ''
                                        }}
                                    </span>
                                </div>
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem v-if="!projects.length" disabled>
                            <span class="text-muted-foreground"
                                >Aucun projet</span
                            >
                        </DropdownMenuItem>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem @click="showCreateDialog = true">
                        <Plus class="mr-2 size-4" />
                        Nouveau projet
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </SidebarMenuItem>
    </SidebarMenu>

    <CreateProjectDialog v-model:open="showCreateDialog" />
</template>
